/**
 * Bridge to host environment (shared shape with ferris-game/src/bridge.js and
 * monkey-wheel-game/src/bridge.js).
 *  - Inside Flutter WebView: window.FlutterChannel postMessage for haptics,
 *    balance refresh, close.
 *  - In a normal browser: stubs to no-ops.
 *  - Auth token: read from URL ?token=, refreshed via window.__setAuthToken.
 *
 * NOTE: the ATMLive source this game came from identified the player with a
 * `?id=<mongo _id>` query param that the server trusted verbatim, and let the
 * client compute and report its own winnings. Both are gone. Identity is
 * proven only by the Firebase ID token below, which the backend verifies on
 * every REST call and on the socket handshake; payouts are computed and
 * credited server-side from the persisted round.
 */

const params = new URLSearchParams(window.location.search);

export const config = {
  backendUrl:
    import.meta.env.VITE_BACKEND_URL ||
    params.get('backend') ||
    'https://nishilive.asadsoft.cloud',
  socketUrl: import.meta.env.VITE_SOCKET_URL || params.get('socket') || null, // defaults to backendUrl
  initialToken: params.get('token') || null,
  debug: params.get('debug') === '1',
};

const isFlutter = typeof window.FlutterChannel !== 'undefined';

export function postFlutter(payload) {
  if (!isFlutter) return;
  try { window.FlutterChannel.postMessage(JSON.stringify(payload)); } catch (e) {}
}

export function haptic(kind = 'light') { postFlutter({ type: 'haptic', kind }); }
export function requestBalanceRefresh() { postFlutter({ type: 'balanceChanged' }); }
export function closeGame() { postFlutter({ type: 'close' }); }

let cachedToken = config.initialToken;
const tokenWaiters = [];

// Flutter calls this back when the token is refreshed.
window.__setAuthToken = (token) => {
  cachedToken = token;
  while (tokenWaiters.length) tokenWaiters.shift()(token);
};

export async function getAuthToken() {
  if (cachedToken) return cachedToken;
  if (!isFlutter) return null;
  postFlutter({ type: 'requestAuthToken' });
  return new Promise((resolve) => {
    const t = setTimeout(() => resolve(null), 4000);
    tokenWaiters.push((tok) => { clearTimeout(t); resolve(tok); });
  });
}

/**
 * Drop the cached token and ask Flutter for a fresh one. The `?token=` we were
 * launched with is a Firebase ID token that expires after an hour, and nothing
 * refreshed it — every REST call (bets, resync) then failed with 401.
 */
export async function refreshAuthToken() {
  cachedToken = null;
  return getAuthToken();
}

export const isInFlutter = isFlutter;
